import React, { useState } from 'react';
import DimensionResults from './DimensionResults';

const ResultsScreen = ({ results }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' atau 'dimensions'
  
  const {
    dimensionScores,
    dimensionConsistency,
    averageConsistency,
    finalScore,
    maxPossibleScore,
    scorePercentage,
    selectedDimension,
    traitCounts // Data baru dari sistem penilaian yang diperbarui
  } = results;

  // Count questions in each dimension for score calculation
  const totalQuestionsInDimension = Object.keys(dimensionConsistency).reduce((acc, dimension) => {
    acc[dimension] = results.dimensionGroups ? results.dimensionGroups[dimension].length : 0;
    return acc;
  }, {});

  // Function to get a text interpretation of consistency
  const getConsistencyRating = (percentage) => {
    if (percentage >= 90) return 'Sangat Konsisten';
    if (percentage >= 75) return 'Konsisten';
    if (percentage >= 60) return 'Cukup Konsisten';
    if (percentage >= 50) return 'Kurang Konsisten';
    return 'Tidak Konsisten';
  };

  // Function to get color class based on consistency percentage
  const getConsistencyColorClass = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-green-500';
    if (percentage >= 60) return 'text-yellow-500';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-500';
  };

  // Get score interpretation
  const getScoreInterpretation = (percentage) => {
    if (percentage >= 85) return 'Sangat Baik';
    if (percentage >= 70) return 'Baik';
    if (percentage >= 60) return 'Cukup';
    if (percentage >= 50) return 'Kurang';
    return 'Perlu Perbaikan';
  };

  // Get explanation based on score
  const getExplanation = (percentage) => {
    if (percentage >= 85) {
      return 'Anda menunjukkan tingkat learning agility yang sangat baik dan konsistensi yang tinggi dalam menjawab. Hal ini mencerminkan kemampuan adaptasi dan belajar yang sangat baik, yang sangat dihargai di lingkungan BUMN.';
    }
    if (percentage >= 70) {
      return 'Anda menunjukkan learning agility yang baik dengan konsistensi jawaban yang memadai. Ini menunjukkan kemampuan Anda untuk beradaptasi dan belajar dengan baik di lingkungan kerja yang dinamis.';
    }
    if (percentage >= 60) {
      return 'Anda menunjukkan learning agility yang cukup. Ada beberapa inkonsistensi dalam jawaban Anda yang mungkin menunjukkan area untuk pengembangan lebih lanjut dalam kemampuan adaptasi Anda.';
    }
    if (percentage >= 50) {
      return 'Hasil tes menunjukkan adanya inkonsistensi yang signifikan dalam jawaban Anda. Hal ini mungkin menunjukkan kebutuhan untuk mengembangkan kemampuan adaptasi dan konsistensi dalam berbagai situasi.';
    }
    return 'Hasil tes menunjukkan tingkat inkonsistensi yang tinggi dalam jawaban Anda, yang dapat mengindikasikan kesulitan dalam beradaptasi dengan situasi yang berbeda atau kurangnya pemahaman tentang kecenderungan pribadi Anda.';
  };

  // Helper function to convert dimension ID to readable title
  const dimensionGroupToTitle = (dimensionId) => {
    const titles = {
      'structureVsFlexibility': 'Struktur vs Fleksibilitas',
      'introvertVsExtrovert': 'Introvert vs Ekstrovert',
      'practicalVsConceptual': 'Praktis vs Konseptual',
      'riskVsCaution': 'Pengambil Risiko vs Kehati-hatian',
      'independentVsCollaborative': 'Mandiri vs Kolaboratif'
    };
    
    return titles[dimensionId] || dimensionId;
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">
              {selectedDimension 
                ? `Hasil Tes Dimensi ${dimensionGroupToTitle(selectedDimension)}` 
                : 'Hasil Tes Learning Agility'}
            </h1>
          </div>
          
          {/* Navigation Tabs - only show if not a single dimension test */}
          {!selectedDimension && (
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Ringkasan
              </button>
              <button
                onClick={() => setActiveTab('dimensions')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'dimensions'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Profil Dimensi
              </button>
            </div>
          )}
          
          {/* Content based on active tab or single dimension */}
          <div className="p-6">
            {/* For single dimension test, always show dimension details */}
            {selectedDimension ? (
              <DimensionResults 
                dimensionScores={dimensionScores} 
                dimensionConsistency={dimensionConsistency}
                totalQuestionsInDimension={totalQuestionsInDimension}
                filterDimension={selectedDimension}
                traitCounts={traitCounts} // Meneruskan traitCounts
              />
            ) : (
              /* For full test, show tab content */
              activeTab === 'overview' ? (
                <>
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Skor Akhir</h2>
                    <div className="flex items-center">
                      <div className="w-24 h-24 rounded-full flex items-center justify-center bg-blue-100 border-4 border-blue-500">
                        <span className="text-2xl font-bold text-blue-700">{scorePercentage}%</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-lg font-medium text-gray-800">
                          {getScoreInterpretation(scorePercentage)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Skor: {finalScore} dari {maxPossibleScore} poin
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Konsistensi Jawaban</h2>
                    <div className="bg-gray-100 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700">Rata-rata Konsistensi:</span>
                        <span className={`font-medium ${getConsistencyColorClass(averageConsistency)}`}>
                          {Math.round(averageConsistency)}% - {getConsistencyRating(averageConsistency)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Konsistensi jawaban Anda mempengaruhi skor akhir. Semakin konsisten jawaban Anda, semakin akurat hasil tes ini.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Interpretasi Hasil</h2>
                    <div className="bg-gray-100 rounded-lg p-4">
                      <p className="text-gray-700">{getExplanation(scorePercentage)}</p>
                    </div>
                  </div>
                </>
              ) : (
                <DimensionResults 
                  dimensionScores={dimensionScores} 
                  dimensionConsistency={dimensionConsistency}
                  totalQuestionsInDimension={totalQuestionsInDimension}
                  traitCounts={traitCounts} // Meneruskan traitCounts 
                />
              )
            )}
            
            <div className="mt-8 flex space-x-4">
              <a
                href="/"
                className="flex-1 text-center py-3 px-6 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                Mulai Tes Baru
              </a>

              {/* Show option to try a different dimension */}
              {selectedDimension && (
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.removeItem('bumnTestProgress');
                    window.location.href = '/';
                  }}
                  className="flex-1 text-center py-3 px-6 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Coba Dimensi Lain
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsScreen;